import { useMemo, useRef, useState } from "react";
import Papa from "papaparse";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Upload, Download, Loader2, Phone, Mail, Globe, FileSpreadsheet, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const KIND_ICON = { phone: Phone, email: Mail, ip: Globe };
const MAX_ROWS = 500;

export default function BulkCsv({ onDone }) {
  const [kind, setKind] = useState("email");
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState([]);
  const inputRef = useRef(null);

  const onFile = (file) => {
    if (!file) return;
    Papa.parse(file, {
      complete: (parsed) => {
        const flat = parsed.data
          .flat()
          .map((v) => String(v || "").trim())
          .filter((v) => v.length > 0)
          .slice(0, MAX_ROWS);
        if (flat.length === 0) { toast.error("No values found in the CSV"); return; }
        setRows(flat);
        setResults([]);
        toast.success(`${flat.length} row${flat.length > 1 ? "s" : ""} loaded`);
      },
      error: (err) => toast.error(err.message || "Could not parse CSV"),
    });
  };

  const clear = () => { setRows([]); setResults([]); if (inputRef.current) inputRef.current.value = ""; };

  const run = async () => {
    if (rows.length === 0) { toast.error("Upload a CSV first"); return; }
    setRunning(true);
    setResults([]);
    try {
      const { data } = await api.post("/validate/bulk", { kind, values: rows });
      setResults(data.results || []);
      toast.success(`${data.count} verifications · ${data.credits_used} credits used`);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };

  const download = () => {
    if (results.length === 0) return;
    const csv = Papa.unparse(results);
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `goodguys-${kind}-results.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const summary = useMemo(() => {
    if (results.length === 0) return null;
    if (kind === "email") {
      const deliverable = results.filter((r) => r.status === "deliverable").length;
      const risky = results.filter((r) => r.status === "risky").length;
      const bad = results.length - deliverable - risky;
      return [{ label: "Deliverable", value: deliverable, tone: "emerald" }, { label: "Risky", value: risky, tone: "amber" }, { label: "Undeliverable", value: bad, tone: "rose" }];
    }
    if (kind === "phone") {
      const clean = results.filter((r) => r.spam_risk === "low").length;
      const risky = results.filter((r) => r.spam_risk === "medium").length;
      const bad = results.length - clean - risky;
      return [{ label: "Clean", value: clean, tone: "emerald" }, { label: "Medium risk", value: risky, tone: "amber" }, { label: "High risk", value: bad, tone: "rose" }];
    }
    const clean = results.filter((r) => r.risk_level === "low").length;
    const risky = results.filter((r) => r.risk_level === "medium").length;
    const bad = results.length - clean - risky;
    return [{ label: "Trusted", value: clean, tone: "emerald" }, { label: "Suspicious", value: risky, tone: "amber" }, { label: "Blocked", value: bad, tone: "rose" }];
  }, [results, kind]);

  const Icon = KIND_ICON[kind];

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6" data-testid="bulk-csv-card">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-semibold text-lg flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-cyan-signal" /> Bulk CSV verification</h3>
          <p className="text-sm text-slate-400 mt-0.5">Drop up to {MAX_ROWS} values per file · 1 credit each.</p>
        </div>
        <Tabs value={kind} onValueChange={(v) => { setKind(v); clear(); }}>
          <TabsList className="bg-slate-950 border border-slate-800 gap-1 h-auto p-1">
            {["phone", "email", "ip"].map((k) => {
              const I = KIND_ICON[k];
              return (
                <TabsTrigger key={k} value={k} data-testid={`bulk-tab-${k}`} className="font-mono text-xs uppercase data-[state=active]:bg-cyan-500/10 data-[state=active]:text-cyan-signal px-3">
                  <I className="w-3.5 h-3.5 mr-1.5" /> {k}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
      </div>

      <label
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
        className="relative flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-700 hover:border-cyan-500/40 bg-slate-950/50 px-6 py-10 cursor-pointer transition-colors"
        data-testid="bulk-drop-zone"
      >
        <input
          ref={inputRef}
          data-testid="bulk-file-input"
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])}
        />
        <Upload className="w-8 h-8 text-slate-500 mb-3" />
        <p className="text-sm text-slate-300">Drop a CSV or <span className="text-cyan-signal underline">choose a file</span></p>
        <p className="text-xs text-slate-500 mt-1 font-mono">One {kind} per row · header optional</p>
      </label>

      {rows.length > 0 && (
        <div className="mt-4 flex items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-950/50 px-4 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className="w-4 h-4 text-cyan-signal flex-shrink-0" />
            <span className="font-mono text-sm text-slate-200 truncate">
              {rows.length} row{rows.length > 1 ? "s" : ""} · first: {rows[0]}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={clear} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></Button>
            <Button data-testid="btn-bulk-run" size="sm" onClick={run} disabled={running} className="bg-cyan-signal text-slate-950 hover:bg-cyan-400 font-semibold">
              {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying</> : <>Verify {rows.length} → {rows.length} credits</>}
            </Button>
          </div>
        </div>
      )}

      {summary && (
        <div className="mt-4 grid grid-cols-3 gap-2">
          {summary.map((s) => {
            const toneCls = s.tone === "emerald" ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/5"
              : s.tone === "amber" ? "text-amber-400 border-amber-500/30 bg-amber-500/5"
              : "text-rose-400 border-rose-500/30 bg-rose-500/5";
            return (
              <div key={s.label} className={`rounded-lg border p-3 ${toneCls}`}>
                <div className="text-2xl font-display font-bold tracking-tight">{s.value}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest mt-0.5">{s.label}</div>
              </div>
            );
          })}
        </div>
      )}

      {results.length > 0 && (
        <div className="mt-4 flex justify-end">
          <Button data-testid="btn-bulk-download" size="sm" variant="outline" onClick={download} className="border-slate-700 bg-slate-900 hover:bg-slate-800">
            <Download className="w-4 h-4 mr-1.5" /> Download CSV ({results.length})
          </Button>
        </div>
      )}
    </div>
  );
}
